import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/utils/apiErrors';
import Icon from '../../../../components/AppIcon';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '../../../../components/ui/Dialog';
import { createMatch, updateMatch, updateMatchAttachment, getMatch } from '@/api/TournamentManagement/matchApi';
import MatchAttachmentField, { type MatchAttachmentFieldState } from './MatchAttachmentField';
import { estimatePoints, getMatchDurations } from '@/api/TournamentManagement/workPointApi';
import {
  MatchType,
  MatchFormat,
  MatchRole,
  AgeCategory,
  resolveEnum,
  DEFAULT_MATCH_FORMAT_DURATIONS,
  MATCH_DURATION_MIN_MINUTES,
  MATCH_DURATION_MAX_MINUTES,
  durationForFormat,
} from '@/models/tournament';
import type { MatchView, CreateMatchInput, UpdateMatchInput, WorkPointEstimateView, MatchFormatDurationsView } from '@/models/tournament';
import { useAllReferees } from '@/hooks/useReferees';
import { useAllTournaments } from '@/hooks/useTournaments';
import { useCurrency } from '@/hooks/useCurrency';
import { wallClockDateParam, wallClockTimeParam } from '@/utils/apiDate';
import { DateField, TimeField } from '@/components/dateFields';

// ── Types ──────────────────────────────────────────────────────────────────

interface TeamMember {
  id: string;
  userId: string;
  name: string;
  role: MatchRole | string;
  workPointOverride: number | undefined;
}

interface FormData {
  tournamentId: string;
  team1: string;
  team2: string;
  location: string;
  pitch: string;
  ageCategory: string;
  division: string;
  date: string;
  time: string;
  timeUnknown: boolean;
  matchType: string;
  matchFormat: string;
  /** Optional per-match duration override in minutes; blank = the format's company duration. */
  duration: string;
}

interface FormErrors {
  [key: string]: string;
}

interface MatchFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  /** Pre-selected tournament (from URL param). Hides tournament dropdown when provided. */
  tournamentId?: number;
  /** When provided, modal operates in edit mode. */
  match?: MatchView | null;
}

// ── Helpers ────────────────────────────────────────────────────────────────

const MATCH_FORMAT_MAP: Record<string, MatchFormat> = {
  '5 aside': MatchFormat.FiveAside,
  '7 aside': MatchFormat.SevenAside,
  '9 aside': MatchFormat.NineAside,
  '11 aside': MatchFormat.ElevenAside,
};

const MATCH_FORMAT_REVERSE: Record<number, string> = {
  [MatchFormat.FiveAside]: '5 aside',
  [MatchFormat.SevenAside]: '7 aside',
  [MatchFormat.NineAside]: '9 aside',
  [MatchFormat.ElevenAside]: '11 aside',
};

const FORMAT_OPTIONS: Record<string, string[]> = {
  Indoor: ['5 aside', '7 aside'],
  Outdoor: ['7 aside', '9 aside', '11 aside'],
};

const ROLE_LABELS: Record<string, string> = {
  [String(MatchRole.HeadReferee)]: 'Head Referee',
  [String(MatchRole.AssistantReferee)]: 'Assistant Referee',
  [String(MatchRole.FourthOfficial)]: 'Fourth Official',
  [String(MatchRole.Commissioner)]: 'Commissioner',
};

// ── Component ──────────────────────────────────────────────────────────────

const MatchFormModal: React.FC<MatchFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  tournamentId: externalTournamentId,
  match,
}) => {
  const { t } = useTranslation('tournament');
  const isEdit = !!match;

  // ── State ──

  const emptyForm = useMemo<FormData>(() => ({
    tournamentId: externalTournamentId ? String(externalTournamentId) : '',
    team1: '',
    team2: '',
    location: '',
    pitch: '',
    ageCategory: '',
    division: '',
    date: '',
    time: '',
    timeUnknown: false,
    matchType: '',
    matchFormat: '',
    duration: '',
  }), [externalTournamentId]);

  const [formData, setFormData] = useState<FormData>(emptyForm);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [formError, setFormError] = useState('');
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Inline add-referee row state
  const [addRole, setAddRole] = useState('');
  const [addUserId, setAddUserId] = useState('');
  const [addPoints, setAddPoints] = useState('');
  const [editingMemberId, setEditingMemberId] = useState<string | null>(null);

  // Attachment state (edit mode only)
  const [attachmentState, setAttachmentState] = useState<MatchAttachmentFieldState>({ file: null, remove: false, dirty: false });
  const handleAttachmentChange = useCallback((s: MatchAttachmentFieldState) => setAttachmentState(s), []);

  // Per-member work-point preview (presumed calculation from current rule)
  const [memberPreviews, setMemberPreviews] = useState<Record<string, WorkPointEstimateView | null>>({});

  // ── Data hooks ──

  const tournamentQuery = useMemo(() => ({}), []);
  const { data: tournamentData } = useAllTournaments(tournamentQuery);
  const tournaments = tournamentData?.items ?? [];

  const { data: refereeData } = useAllReferees();
  const availableUsers = useMemo(
    () => (refereeData?.items ?? []).map(r => ({ id: r.userId, name: r.name })),
    [refereeData],
  );
  // Map userId -> referee profile id, so the preview estimate can pull the real performance bonus
  const profileIdByUserId = useMemo(() => {
    const map: Record<string, number> = {};
    (refereeData?.items ?? []).forEach(r => { map[r.userId] = r.id; });
    return map;
  }, [refereeData]);
  const { format: formatCurrencyAmount } = useCurrency();

  // Company-wide durations, only used to show what a blank Duration field means. Falls back to the
  // built-in defaults if the settings cannot be read — the server resolves the real value either way.
  const [companyDurations, setCompanyDurations] = useState<MatchFormatDurationsView>(DEFAULT_MATCH_FORMAT_DURATIONS);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    getMatchDurations()
      .then(result => {
        if (!cancelled && result.errors.length === 0 && result.payload) setCompanyDurations(result.payload);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen]);

  // ── Populate on edit ──

  // The edit dialog paints from the LIST row first and then re-seeds from GetMatch. That second seed
  // REPLACED the whole form, so anything the user typed while the fetch was still in flight was
  // silently reverted — and the save then wrote the pre-edit values back with no error at all.
  // Measured (verify-2026-09-17 spec 07 K1, trace): the dialog mounted at 19:38:51.055, GetMatch took
  // 187 ms because five sibling requests fire on the same mount, the pitch was typed at ~51.10, the
  // re-seed landed at ~51.242 and the PUT at 51.303 carried NO pitch field. These two refs let the
  // late seed detect that the form has moved on and leave it alone; `seededRef` is what we last wrote,
  // `liveRef` is what the form holds now, so ANY user mutation (typing, roster add/remove, work-point
  // override) makes them differ without each handler having to remember to flag itself.
  const seededRef = useRef<string>('');
  const liveRef = useRef<{ f: FormData; tm: TeamMember[] }>({ f: emptyForm, tm: [] });
  useEffect(() => { liveRef.current = { f: formData, tm: teamMembers }; });

  const populateForm = useCallback((m: MatchView) => {
    const nextForm: FormData = {
      tournamentId: String(m.tournamentId),
      team1: m.team1Name || '',
      team2: m.team2Name || '',
      location: m.location || '',
      pitch: m.pitch || '',
      ageCategory: m.ageCategory != null ? String(resolveEnum(AgeCategory, m.ageCategory) ?? '') : '',
      division: m.division || '',
      // Wall-clock round-trip: toISOString() shifted the DATE for any viewer east of UTC (a 01:00
      // UAE kickoff opened the edit form on the previous day).
      date: wallClockDateParam(m.scheduledDateTime),
      time: m.scheduledTimeUnknown ? '' : wallClockTimeParam(m.scheduledDateTime),
      timeUnknown: !!m.scheduledTimeUnknown,
      matchType: resolveEnum(MatchType, m.type) === MatchType.Indoor ? 'Indoor' : 'Outdoor',
      matchFormat: MATCH_FORMAT_REVERSE[resolveEnum(MatchFormat, m.format) ?? 0] ?? '',
      // Only a per-match override is shown as a value; a format duration stays blank so the match keeps
      // following the format (and re-resolves if the format changes).
      duration: m.durationOverridden && m.durationMinutes ? String(m.durationMinutes) : '',
    };

    const nextMembers: TeamMember[] = (m.refereeTeamMembers ?? []).map(tm => ({
      id: String(tm.id),
      userId: tm.userId,
      name: tm.userName,
      role: String(resolveEnum(MatchRole, tm.role) ?? tm.role),
      workPointOverride: tm.workPointOverride ?? undefined,
    }));

    setFormData(nextForm);
    setTeamMembers(nextMembers);
    // Keep liveRef in step synchronously. The effect above only runs after the next render, and a fast
    // GetMatch can resolve before that — which would read a stale liveRef, look like a user edit, and
    // skip a seed that was actually safe to apply.
    liveRef.current = { f: nextForm, tm: nextMembers };
    seededRef.current = JSON.stringify(liveRef.current);
  }, []);

  // Compute work-point preview for every assigned team member whenever match
  // parameters change. Uses backend EstimatePoints so it always reflects the
  // currently-active rule.
  useEffect(() => {
    if (!isOpen) return;
    const fmt = MATCH_FORMAT_MAP[formData.matchFormat];
    const typ = formData.matchType === 'Indoor' ? MatchType.Indoor : (formData.matchType === 'Outdoor' ? MatchType.Outdoor : undefined);
    if (fmt == null || typ == null || !formData.date || !formData.time || teamMembers.length === 0) {
      if (Object.keys(memberPreviews).length > 0) setMemberPreviews({});
      return;
    }
    // Local wall-clock format — the server compares scheduledTime.Hour against the
    // rule's night/early-morning thresholds, so we must preserve the hour the user
    // picked. toISOString() would UTC-shift it.
    const localIso = `${formData.date}T${formData.time}:00`;
    let cancelled = false;
    const next: Record<string, WorkPointEstimateView | null> = {};
    Promise.all(teamMembers.map(async (m) => {
      const role = (isNaN(parseInt(m.role, 10)) ? resolveEnum(MatchRole, m.role) ?? MatchRole.HeadReferee : parseInt(m.role, 10)) as MatchRole;
      try {
        const result = await estimatePoints({
          role,
          format: fmt,
          type: typ,
          scheduledDateTime: localIso,
          refereeProfileId: profileIdByUserId[m.userId],
        });
        if (!cancelled && result.errors.length === 0 && result.payload) {
          next[m.id] = result.payload as WorkPointEstimateView;
        }
      } catch {
        // leave preview absent for this member
      }
    })).then(() => {
      if (!cancelled) setMemberPreviews(next);
    });
    return () => { cancelled = true; };
    // memberPreviews intentionally excluded — we read it to guard the clear-state path
    // but including it would re-trigger the effect whenever we set it, causing an infinite loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, teamMembers, formData.matchFormat, formData.matchType, formData.date, formData.time, profileIdByUserId]);

  useEffect(() => {
    if (!isOpen) return;
    if (!match) {
      // Add-mode: ensure a clean slate every time the modal opens
      setFormData(emptyForm);
      setTeamMembers([]);
      resetAddRow();
      setErrors({});
      setFormError('');
      setApiError('');
      return;
    }
    let cancelled = false;
    populateForm(match);
    getMatch(match.id)
      .then(result => {
        if (cancelled || result.errors.length > 0 || !result.payload) return;
        // Never overwrite an edit already in progress — see the seededRef/liveRef note above.
        if (JSON.stringify(liveRef.current) !== seededRef.current) return;
        populateForm(result.payload);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [isOpen, match, populateForm, emptyForm]);

  // ── Handlers ──

  const handleChange = (field: keyof FormData, value: string) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'matchType') next.matchFormat = '';
      return next;
    });
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  // Toggle "kickoff time unknown" — clears the time field and its validation error.
  const toggleTimeUnknown = (checked: boolean) => {
    setFormData(prev => ({ ...prev, timeUnknown: checked, time: checked ? '' : prev.time }));
    if (checked && errors.time) setErrors(prev => ({ ...prev, time: '' }));
  };

  const resetAddRow = () => {
    setAddRole('');
    setAddUserId('');
    setAddPoints('');
    setEditingMemberId(null);
  };

  const handleAddOrUpdateMember = () => {
    if (!addUserId || !addRole) {
      setFormError(t('Please fill in all team member fields'));
      return;
    }
    setFormError('');

    const user = availableUsers.find(u => u.id === addUserId);
    if (!user) return;

    if (editingMemberId) {
      setTeamMembers(prev =>
        prev.map(m =>
          m.id === editingMemberId
            ? { ...m, userId: addUserId, name: user.name, role: addRole, workPointOverride: addPoints !== '' ? Number(addPoints) : undefined }
            : m,
        ),
      );
    } else {
      setTeamMembers(prev => [
        ...prev,
        {
          id: `TM${Math.floor(Math.random() * 9000) + 1000}`,
          userId: addUserId,
          name: user.name,
          role: addRole,
          workPointOverride: addPoints !== '' ? Number(addPoints) : undefined,
        },
      ]);
    }
    resetAddRow();
    if (errors.teamMembers) setErrors(prev => ({ ...prev, teamMembers: '' }));
  };

  const handleEditMember = (member: TeamMember) => {
    setAddRole(String(member.role));
    setAddUserId(member.userId);
    setAddPoints(member.workPointOverride != null ? String(member.workPointOverride) : '');
    setEditingMemberId(member.id);
  };

  const handleRemoveMember = (id: string) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
    if (editingMemberId === id) resetAddRow();
  };

  // ── Validation ──

  const validate = (): boolean => {
    const e: FormErrors = {};
    if (!isEdit && !externalTournamentId && !formData.tournamentId)
      e.tournamentId = t('Tournament is required');
    if (!formData.team1) e.team1 = t('Team 1 is required');
    if (!formData.team2) e.team2 = t('Team 2 is required');
    if (!formData.location) e.location = t('Location is required');
    if (!formData.date) e.date = t('Date is required');
    if (!formData.timeUnknown && !formData.time) e.time = t('Time is required');
    if (!formData.matchType) e.matchType = t('Match type is required');
    if (!formData.matchFormat) e.matchFormat = t('Match format is required');
    if (formData.duration.trim() !== '') {
      const minutes = Number(formData.duration);
      if (!Number.isInteger(minutes) || minutes < MATCH_DURATION_MIN_MINUTES || minutes > MATCH_DURATION_MAX_MINUTES) {
        e.duration = t('Duration must be a whole number between {{min}} and {{max}} minutes', {
          min: MATCH_DURATION_MIN_MINUTES,
          max: MATCH_DURATION_MAX_MINUTES,
        });
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Submit ──

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setApiError('');
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      const scheduledDateTime = `${formData.date}T${formData.timeUnknown ? '00:00' : formData.time}:00`;
      const durationMinutesOverride = formData.duration.trim() !== '' ? Number(formData.duration) : undefined;

      if (isEdit && match) {
        const input: UpdateMatchInput = {
          id: match.id,
          team1Name: formData.team1,
          team2Name: formData.team2,
          location: formData.location,
          pitch: formData.pitch || undefined,
          ageCategory: formData.ageCategory ? parseInt(formData.ageCategory, 10) as AgeCategory : undefined,
          division: formData.division || undefined,
          scheduledDateTime,
          scheduledTimeUnknown: formData.timeUnknown,
          type: formData.matchType === 'Indoor' ? MatchType.Indoor : MatchType.Outdoor,
          format: MATCH_FORMAT_MAP[formData.matchFormat] ?? MatchFormat.FiveAside,
          durationMinutesOverride,
          // A blank field on a match that HAD an override means "back to the company default"; the server
          // treats an omitted override as "keep" so older clients cannot wipe it by accident.
          resetDurationToDefault: durationMinutesOverride === undefined && !!match.durationOverridden,
          refereeTeamMembers: teamMembers.map(m => ({
            userId: m.userId,
            role: (isNaN(parseInt(m.role, 10)) ? resolveEnum(MatchRole, m.role) ?? MatchRole.HeadReferee : parseInt(m.role, 10)) as MatchRole,
            workPointOverride: m.workPointOverride != null ? m.workPointOverride : undefined,
          })),
        };
        const result = await updateMatch(input);
        if (result.errors.length > 0) {
          setApiError(result.errors[0]?.description || t('Failed to update match'));
          return;
        }
        // Two-step save: apply attachment changes if any.
        if (attachmentState.dirty) {
          try {
            const attachResult = await updateMatchAttachment({
              matchId: match.id,
              file: attachmentState.file,
              remove: attachmentState.remove,
            });
            if (attachResult.errors.length > 0) {
              setApiError(attachResult.errors[0]?.description || t('Match saved. Image upload failed — please retry.'));
              return;
            }
          } catch {
            setApiError(t('Match saved. Image upload failed — please retry.'));
            return;
          }
        }
      } else {
        const tid = externalTournamentId || parseInt(formData.tournamentId, 10);
        if (!tid) {
          setApiError(t('Tournament ID is missing. Please select a tournament.'));
          return;
        }
        const input: CreateMatchInput = {
          tournamentId: tid,
          team1Name: formData.team1,
          team2Name: formData.team2,
          location: formData.location,
          pitch: formData.pitch || undefined,
          ageCategory: formData.ageCategory ? parseInt(formData.ageCategory, 10) as AgeCategory : undefined,
          division: formData.division || undefined,
          scheduledDateTime,
          scheduledTimeUnknown: formData.timeUnknown,
          type: formData.matchType === 'Indoor' ? MatchType.Indoor : MatchType.Outdoor,
          format: MATCH_FORMAT_MAP[formData.matchFormat] ?? MatchFormat.FiveAside,
          durationMinutesOverride,
          refereeTeamMembers: teamMembers.map(m => ({
            userId: m.userId,
            role: (isNaN(parseInt(m.role, 10)) ? resolveEnum(MatchRole, m.role) ?? MatchRole.HeadReferee : parseInt(m.role, 10)) as MatchRole,
            workPointOverride: m.workPointOverride != null ? m.workPointOverride : undefined,
          })),
        };
        const result = await createMatch(input);
        if (result.errors.length > 0) {
          setApiError(result.errors[0]?.description || t('Failed to create match'));
          return;
        }
      }

      onSuccess();
      handleClose();
    } catch (error) {
      // Axios rejects non-2xx, so a 400 refusal (e.g. an overlapping referee booking) arrives HERE, not in
      // result.errors — show the server's reason, falling back to the generic text.
      setApiError(getApiErrorMessage(error, t,
        t(isEdit ? 'Failed to update match. Please try again.' : 'Failed to create match. Please try again.'), ' '));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData(emptyForm);
    setTeamMembers([]);
    resetAddRow();
    setErrors({});
    setFormError('');
    setApiError('');
    onClose();
  };

  if (!isOpen) return null;

  const showTournamentPicker = !isEdit && !externalTournamentId;
  const formatOptions = FORMAT_OPTIONS[formData.matchType] ?? [];
  const selectedFormat = MATCH_FORMAT_MAP[formData.matchFormat];
  const durationPlaceholder = selectedFormat != null
    ? t('durationDefaultPlaceholder', { minutes: durationForFormat(companyDurations, selectedFormat) })
    : t('Format default');

  return (
    // Dismissal (X, Escape, backdrop) is locked together while the submit is in flight, matching
    // the disabled submit button.
    <Dialog open onOpenChange={(open) => { if (!open && !isSubmitting) handleClose(); }}>
      {/* size="lg" + max-w-3xl keeps the original width; overflow-y-hidden hands scrolling back to
          the form so header and footer stay put. The header X is kept (hideCloseButton) so its
          position and data-testid survive. */}
      <DialogContent
        size="lg"
        hideCloseButton
        className="max-w-3xl flex flex-col overflow-y-hidden"
        data-testid={isEdit ? 'edit-match-modal' : 'add-match-modal'}
      >
        {/* ── Header ── */}
        <DialogHeader className="py-4 shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg flex items-center gap-2">
              <Icon name={isEdit ? 'Edit' : 'Plus'} size={20} className="text-primary" />
              {t(isEdit ? 'Edit Match' : 'Add New Match')}
            </DialogTitle>
            <button
              type="button"
              onClick={handleClose}
              aria-label={t('Close')}
              className="p-2 hover:bg-muted rounded-lg transition-colors focus-visible:ring-2 focus-visible:ring-primary/20"
              data-testid={isEdit ? 'edit-match-close-btn' : 'add-match-close-btn'}
            >
              <Icon name="X" size={18} />
            </button>
          </div>
        </DialogHeader>

        {/* ── Body ── */}
        <form id="match-form" onSubmit={handleSubmit} className="overflow-y-auto flex-1">
          <DialogBody className="space-y-5">
            {/* Section 1: Tournament (add mode, no URL id) */}
            {showTournamentPicker && (
              <div>
                <label htmlFor="match-tournament" className="block text-sm font-medium text-foreground mb-1.5">
                  {t('Tournament')} *
                </label>
                {/* `error` (not a className) is what paints the invalid border on ui/Select — the old
                    `border-destructive` landed on the wrapper div, which has no border width.
                    `aria-required` rather than `required`: ui/Select puts `required` on its hidden
                    native <select>, which would block form submission with an unfocusable control. */}
                <Select
                  id="match-tournament"
                  value={formData.tournamentId}
                  onChange={(v: string) => handleChange('tournamentId', v)}
                  options={tournaments.map(tour => ({ value: String(tour.id), label: tour.name }))}
                  placeholder={t('Select tournament')}
                  error={errors.tournamentId}
                  aria-required
                  aria-invalid={errors.tournamentId ? true : undefined}
                  data-testid="match-tournament-select"
                />
              </div>
            )}

            {/* Show tournament name as chip when provided via URL */}
            {!isEdit && externalTournamentId && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon name="Trophy" size={16} className="text-primary" />
                <span>
                  {tournaments.find(tour => tour.id === externalTournamentId)?.name ??
                    `${t('Tournament')} #${externalTournamentId}`}
                </span>
              </div>
            )}

            {/* Section 2: Match Details */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Trophy" size={16} className="text-primary" />
                {t('Match Details')}
              </h3>

              {/* Row 1: Teams */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label htmlFor="match-team1" className="block text-xs font-medium text-foreground mb-1">{t('Team 1')} *</label>
                  {/* ui/Input's `error` prop is what wires id + role="alert" + aria-invalid +
                      aria-describedby; the message used to be an unlinked sibling <p>. */}
                  <Input
                    id="match-team1"
                    type="text"
                    required
                    value={formData.team1}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('team1', e.target.value)}
                    placeholder={t('Enter team 1 name')}
                    error={errors.team1}
                    data-testid={isEdit ? 'edit-match-team1-input' : 'add-match-team1-input'}
                  />
                </div>
                <div>
                  <label htmlFor="match-team2" className="block text-xs font-medium text-foreground mb-1">{t('Team 2')} *</label>
                  <Input
                    id="match-team2"
                    type="text"
                    required
                    value={formData.team2}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('team2', e.target.value)}
                    placeholder={t('Enter team 2 name')}
                    error={errors.team2}
                    data-testid={isEdit ? 'edit-match-team2-input' : 'add-match-team2-input'}
                  />
                </div>
              </div>

              {/* Row 2: Location (full width) */}
              <div>
                <label htmlFor="match-location" className="block text-xs font-medium text-foreground mb-1">{t('Location')} *</label>
                <Input
                  id="match-location"
                  type="text"
                  required
                  value={formData.location}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('location', e.target.value)}
                  placeholder={t('Enter match location')}
                  error={errors.location}
                  data-testid={isEdit ? 'edit-match-location-input' : 'add-match-location-input'}
                />
              </div>

              {/* Row 2b: Pitch · Age Category · Division */}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label htmlFor="match-pitch" className="block text-xs font-medium text-foreground mb-1">{t('Pitch')}</label>
                  <Input
                    id="match-pitch"
                    type="text"
                    value={formData.pitch}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('pitch', e.target.value)}
                    placeholder={t('Enter pitch')}
                    data-testid={isEdit ? 'edit-match-pitch-input' : 'add-match-pitch-input'}
                  />
                </div>
                <div>
                  <label htmlFor="match-age-category" className="block text-xs font-medium text-foreground mb-1">{t('Age Category')}</label>
                  <Select
                    id="match-age-category"
                    value={formData.ageCategory}
                    onChange={(v: string) => handleChange('ageCategory', v)}
                    options={Object.keys(AgeCategory)
                      .filter(k => isNaN(Number(k)))
                      .map(k => ({ value: String(AgeCategory[k as keyof typeof AgeCategory]), label: t(k) }))}
                    placeholder={t('Select age category')}
                    data-testid={isEdit ? 'edit-match-age-category-select' : 'add-match-age-category-select'}
                  />
                </div>
                <div>
                  <label htmlFor="match-division" className="block text-xs font-medium text-foreground mb-1">{t('Division')}</label>
                  <Input
                    id="match-division"
                    type="text"
                    value={formData.division}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('division', e.target.value)}
                    placeholder={t('Enter division')}
                    data-testid={isEdit ? 'edit-match-division-input' : 'add-match-division-input'}
                  />
                </div>
              </div>

              {/* Row 3: Date · Time · Type · Format */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label htmlFor="match-date" className="block text-xs font-medium text-foreground mb-1">{t('Date')} *</label>
                  <DateField
                    id="match-date"
                    required
                    value={formData.date}
                    onChange={(v) => handleChange('date', v)}
                    error={errors.date}
                    data-testid={isEdit ? 'edit-match-date-input' : 'add-match-date-input'}
                  />
                </div>
                <div>
                  <label htmlFor="match-time" className="block text-xs font-medium text-foreground mb-1">
                    {t('Time')}{!formData.timeUnknown && ' *'}
                  </label>
                  <TimeField
                    id="match-time"
                    required={!formData.timeUnknown}
                    value={formData.time}
                    onChange={(v) => handleChange('time', v)}
                    disabled={formData.timeUnknown}
                    error={!formData.timeUnknown ? errors.time : undefined}
                    inputClassName={formData.timeUnknown ? 'opacity-50' : ''}
                    data-testid={isEdit ? 'edit-match-time-input' : 'add-match-time-input'}
                  />
                  <label className="mt-1.5 flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={formData.timeUnknown}
                      onChange={(e) => toggleTimeUnknown(e.target.checked)}
                      className="accent-primary"
                      data-testid={isEdit ? 'edit-match-time-unknown' : 'add-match-time-unknown'}
                    />
                    {t('Time TBD')}
                  </label>
                </div>
                <div>
                  <label htmlFor="match-type" className="block text-xs font-medium text-foreground mb-1">{t('Type')} *</label>
                  <Select
                    id="match-type"
                    value={formData.matchType}
                    onChange={(v: string) => handleChange('matchType', v)}
                    options={[
                      { label: t('Indoor'), value: 'Indoor' },
                      { label: t('Outdoor'), value: 'Outdoor' },
                    ]}
                    placeholder={t('Type')}
                    error={errors.matchType}
                    aria-required
                    aria-invalid={errors.matchType ? true : undefined}
                    data-testid={isEdit ? 'edit-match-type-select' : 'add-match-type-select'}
                  />
                </div>
                <div>
                  <label htmlFor="match-format" className="block text-xs font-medium text-foreground mb-1">{t('Format')} *</label>
                  <Select
                    id="match-format"
                    value={formData.matchFormat}
                    onChange={(v: string) => handleChange('matchFormat', v)}
                    options={formatOptions.map(f => ({ label: t(f), value: f }))}
                    placeholder={t('Format')}
                    disabled={!formData.matchType}
                    error={errors.matchFormat}
                    aria-required
                    aria-invalid={errors.matchFormat ? true : undefined}
                    data-testid={isEdit ? 'edit-match-format-select' : 'add-match-format-select'}
                  />
                </div>
              </div>

              {/* Row 4: optional per-match duration (owner decision 2026-09-17). Blank = the format's
                  company duration, which the placeholder shows. */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <label htmlFor="match-duration" className="block text-xs font-medium text-foreground mb-1">{t('Duration (minutes)')}</label>
                  <Input
                    id="match-duration"
                    type="number"
                    min={MATCH_DURATION_MIN_MINUTES}
                    max={MATCH_DURATION_MAX_MINUTES}
                    step={1}
                    value={formData.duration}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange('duration', e.target.value)}
                    placeholder={durationPlaceholder}
                    error={errors.duration}
                    data-testid={isEdit ? 'edit-match-duration-input' : 'add-match-duration-input'}
                  />
                </div>
                <p className="col-span-1 md:col-span-3 self-end pb-2 text-[11px] text-muted-foreground">
                  {t('Leave blank to use the format duration from Work Point Settings. Referee shifts use this length.')}
                </p>
              </div>
            </div>

            {/* Section 3: Referee Team */}
            <div className="space-y-3 border-t border-border pt-4">
              <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Icon name="Users" size={16} className="text-primary" />
                {t('Referee Team Members')}
              </h3>

              {/* Inline add row */}
              <div className="flex items-end gap-2 bg-muted/50 rounded-lg p-3">
                <div className="flex-1 min-w-0">
                  <label htmlFor="match-add-role" className="block text-xs font-medium text-foreground mb-1">{t('Role')}</label>
                  <Select
                    id="match-add-role"
                    value={addRole}
                    onChange={(v: string) => setAddRole(v)}
                    options={[
                      { value: String(MatchRole.HeadReferee), label: t('Head Referee') },
                      { value: String(MatchRole.AssistantReferee), label: t('Assistant Referee') },
                      { value: String(MatchRole.FourthOfficial), label: t('Fourth Official') },
                      { value: String(MatchRole.Commissioner), label: t('Commissioner') },
                    ]}
                    placeholder={t('Role')}
                    data-testid={isEdit ? 'edit-match-role-select' : 'add-match-role-select'}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <label htmlFor="match-add-member" className="block text-xs font-medium text-foreground mb-1">{t('Member')}</label>
                  <Select
                    id="match-add-member"
                    value={addUserId}
                    onChange={(v: string) => setAddUserId(v)}
                    options={availableUsers.map(u => ({ value: u.id, label: u.name }))}
                    placeholder={t('Member')}
                    searchable
                    data-testid={isEdit ? 'edit-match-member-select' : 'add-match-member-select'}
                  />
                </div>
                <div className="w-20 shrink-0">
                  <label htmlFor="match-add-points" className="block text-xs font-medium text-foreground mb-1">{t('Points Override')}</label>
                  <Input
                    id="match-add-points"
                    type="number"
                    value={addPoints}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddPoints(e.target.value)}
                    placeholder={t('Auto')}
                    data-testid={isEdit ? 'edit-match-workpoints-input' : 'add-match-workpoints-input'}
                  />
                </div>
                <Button
                  type="button"
                  onClick={handleAddOrUpdateMember}
                  aria-label={editingMemberId ? t('Save') : t('addTeamMember')}
                  disabled={!addUserId || !addRole}
                  className="shrink-0 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid={isEdit ? 'edit-match-add-member-btn' : 'add-match-add-member-btn'}
                >
                  <Icon name={editingMemberId ? 'Check' : 'Plus'} size={16} />
                </Button>
              </div>

              {/* Form error for referee validation */}
              {formError && (
                <p role="alert" className="text-sm text-destructive" data-testid={isEdit ? 'edit-match-form-error' : 'add-match-form-error'}>{formError}</p>
              )}

              {/* Added members list — with per-referee breakdown + override preview */}
              {teamMembers.length > 0 && (
                <div className="space-y-2">
                  {teamMembers.map(member => {
                    const preview = memberPreviews[member.id];
                    const calculatedPoints = preview?.estimatedPoints;
                    const overrideValue = member.workPointOverride;
                    const effectivePoints = overrideValue != null ? overrideValue : calculatedPoints;
                    return (
                      <div
                        key={member.id}
                        className={`rounded-lg border transition-all ${
                          editingMemberId === member.id
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                        }`}
                        data-testid={`${isEdit ? 'edit' : 'add'}-match-member-row-${member.id}`}
                      >
                        {/* Header row */}
                        <div
                          className="flex items-center justify-between px-3 py-2 cursor-pointer"
                          onClick={() => handleEditMember(member)}
                        >
                          <div className="flex items-center gap-3 text-sm min-w-0">
                            <Icon name="User" size={14} className="text-muted-foreground shrink-0" />
                            <span className="font-medium text-foreground truncate">{member.name}</span>
                            <span className="text-muted-foreground">·</span>
                            <span className="text-muted-foreground truncate">
                              {t(ROLE_LABELS[String(member.role)] ?? 'Unknown')}
                            </span>
                            {effectivePoints != null && (
                              <>
                                <span className="text-muted-foreground">·</span>
                                <span className={`whitespace-nowrap font-medium ${overrideValue != null ? 'text-amber-700 dark:text-amber-300' : 'text-foreground'}`}>
                                  {effectivePoints} {t('pts')}
                                  {overrideValue != null && (
                                    <span className="text-xs text-muted-foreground ms-1">({t('override')})</span>
                                  )}
                                </span>
                              </>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={e => {
                              e.stopPropagation();
                              handleRemoveMember(member.id);
                            }}
                            aria-label={t('removeTeamMember')}
                            title={t('removeTeamMember')}
                            className="p-1 text-destructive hover:bg-destructive/10 rounded transition-colors shrink-0 focus-visible:ring-2 focus-visible:ring-destructive/20"
                            data-testid={`${isEdit ? 'edit' : 'add'}-match-remove-member-${member.id}`}
                          >
                            <Icon name="X" size={14} />
                          </button>
                        </div>

                        {/* Breakdown + override preview */}
                        {preview && (
                          <div className="border-t border-border px-3 py-2 bg-muted/30 space-y-2">
                            <div className="flex flex-wrap items-center gap-1.5 text-xs">
                              <span className="text-muted-foreground uppercase tracking-wide text-[10px] me-1">{t('Presumed breakdown')}</span>
                              <span className="bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{t('Base')}: {preview.baseRolePoints}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-indigo-50 text-indigo-700 px-1.5 py-0.5 rounded">{t('Format')}: {preview.formatPoints}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">{t('Type')}: {preview.typeBonus}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded">{t('Time')}: {preview.timeBonus}</span>
                              <span className="text-gray-400">+</span>
                              <span className="bg-pink-50 text-pink-700 px-1.5 py-0.5 rounded">{t('Performance')}: {preview.performanceBonus}</span>
                              <span className="text-gray-400">=</span>
                              <span className="bg-primary/10 text-primary px-1.5 py-0.5 rounded font-semibold">
                                {preview.estimatedPoints} {t('pts')}
                              </span>
                              {preview.rawSubtotal !== preview.estimatedPoints && (
                                <span className="text-[11px] text-amber-700">({t('adjusted by caps')} {preview.minPointsPerMatch}–{preview.maxPointsPerMatch})</span>
                              )}
                              <span className="text-[11px] text-muted-foreground">≈ {formatCurrencyAmount(preview.estimatedCurrencyAmount)}</span>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <label htmlFor={`match-override-${member.id}`} className="text-xs text-muted-foreground whitespace-nowrap">
                                {t('Override points')}:
                              </label>
                              <input
                                id={`match-override-${member.id}`}
                                type="number"
                                min={0}
                                value={overrideValue != null ? String(overrideValue) : ''}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  setTeamMembers(prev => prev.map(m =>
                                    m.id === member.id
                                      ? { ...m, workPointOverride: v === '' ? undefined : Number(v) }
                                      : m,
                                  ));
                                }}
                                className="w-24 px-2 py-1 text-xs border border-border rounded bg-background focus-visible:ring-2 focus-visible:ring-primary/20"
                                placeholder={t('None')}
                                data-testid={`${isEdit ? 'edit' : 'add'}-match-override-input-${member.id}`}
                              />
                              {overrideValue != null ? (
                                <span className="text-xs text-amber-700 dark:text-amber-300 inline-flex items-center gap-1">
                                  <Icon name="ArrowRight" size={12} className="rtl:rotate-180" />
                                  {t('Will become')} <span className="font-semibold">{overrideValue} {t('pts')}</span>
                                  <span className="text-muted-foreground">({formatCurrencyAmount(overrideValue * preview.pointToCurrencyRate)})</span>
                                </span>
                              ) : (
                                <span className="text-[11px] text-muted-foreground">{t('Leave blank to use the calculated amount')}</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {teamMembers.length > 0 && Object.keys(memberPreviews).length === 0 && (formData.matchFormat === '' || formData.matchType === '' || !formData.date || !formData.time) && (
                    <p className="text-[11px] text-muted-foreground">
                      {t('Fill in match format, type, date and time above to see the presumed work-point breakdown per referee.')}
                    </p>
                  )}
                </div>
              )}
              {/* Section-level error — it belongs to the referee list as a whole, not to one control,
                  so it is announced with role="alert" rather than wired with aria-describedby. */}
              {errors.teamMembers && <p role="alert" className="text-xs text-destructive">{errors.teamMembers}</p>}
            </div>

            {/* Attachment (edit only — match must exist to attach) */}
            {isEdit && match && (
              <MatchAttachmentField
                existingUrl={match.attachmentUrl ?? null}
                onChange={handleAttachmentChange}
                disabled={isSubmitting}
              />
            )}

            {/* API Error */}
            {apiError && (
              <div role="alert" className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center gap-2" data-testid={isEdit ? 'edit-match-api-error' : 'add-match-api-error'}>
                <Icon name="AlertCircle" size={16} className="text-destructive shrink-0" />
                <p className="text-sm text-destructive">{apiError}</p>
              </div>
            )}
          </DialogBody>
        </form>

        {/* ── Footer ── */}
        <DialogFooter className="py-4 shrink-0">
          <Button
            type="button"
            onClick={handleClose}
            className="px-5 py-2 border border-border text-foreground rounded-lg hover:bg-muted"
            data-testid={isEdit ? 'edit-match-cancel-btn' : 'add-match-cancel-btn'}
          >
            {t('Cancel')}
          </Button>
          <Button
            type="submit"
            form="match-form"
            disabled={isSubmitting}
            className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={isEdit ? 'edit-match-submit-btn' : 'add-match-submit-btn'}
          >
            <Icon
              name={isSubmitting ? 'Loader2' : isEdit ? 'Save' : 'Plus'}
              size={16}
              className={isSubmitting ? 'animate-spin' : ''}
            />
            {isSubmitting
              ? t(isEdit ? 'Updating...' : 'Creating...')
              : t(isEdit ? 'Update Match' : 'Add Match')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MatchFormModal;
